/**
 * Hiding card identities from everyone but their holder.
 *
 * Two layers that must agree exactly:
 *
 *   1. `redactEvent` strips private detail from the incremental event stream.
 *   2. `projectState` strips it from a snapshot.
 *
 * The invariant tying them together is the load-bearing property of the whole
 * sync design:
 *
 *     fold(log.map(e => redactEvent(e, s)), s) === projectState(fold(log), s)
 *
 * Hold that, and a client folding only what it was allowed to see arrives at
 * exactly the state the server would have projected for it -- so no hand can
 * leak and no client can desync on hidden state. It is property-tested.
 */

import type { SeatId } from '../ids.js';
import type { RecordedEvent } from '../events/events.js';
import { handSize, type GameState, type HandView } from './types.js';

/** Who a fold or projection is for. The server folds as `omniscient`. */
export type Viewer = SeatId | 'omniscient';

export function canSee(viewer: Viewer, owner: SeatId): boolean {
  return viewer === 'omniscient' || viewer === owner;
}

/** Strip anything `viewer` is not entitled to see. Public events pass through. */
export function redactEvent(event: RecordedEvent, viewer: Viewer): RecordedEvent {
  switch (event.type) {
    case 'card.drawn':
      if (canSee(viewer, event.payload.seat)) return event;
      return { ...event, payload: { ...event.payload, card: null } };

    case 'card.identified':
      if (canSee(viewer, event.payload.seat)) return event;
      return { ...event, payload: { ...event.payload, card: null } };

    case 'player.eliminated':
      // The captured cards go to the eliminator, so they are the ones who
      // may see them. The public count stays, since hand sizes are public.
      if (canSee(viewer, event.payload.by)) return event;
      return { ...event, payload: { ...event.payload, cards: null } };

    default:
      return event;
  }
}

export function redactLog(
  events: readonly RecordedEvent[],
  viewer: Viewer,
): readonly RecordedEvent[] {
  return events.map((event) => redactEvent(event, viewer));
}

/** Reduce a full state to what `viewer` is entitled to see. */
export function projectState(state: GameState, viewer: Viewer): GameState {
  if (viewer === 'omniscient') return state;

  const hands: Record<SeatId, HandView> = {};
  for (const [seat, hand] of Object.entries(state.hands)) {
    hands[seat] = seat === viewer ? hand : { visible: false, count: handSize(hand) };
  }
  return { ...state, hands };
}
